/**
 * Shared file validation utilities for policy document uploads.
 * Validates MIME type, file extension, and file size before upload.
 */

// PDF-only since F-S80-2 retirement (removal commit referenced in Session Log S82).
// Word docs: already-approved → Import Existing (which converts); new drafts → Composer
// or export to PDF first. Bulk intake (useBulkPolicyIntake) still accepts doc/docx.
const ALLOWED_MIME_TYPES: Record<string, string[]> = {
  'application/pdf': ['pdf'],
};

const ALLOWED_EXTENSIONS = new Set(['pdf']);
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

export interface FileValidationResult {
  valid: boolean;
  error?: string;
}

function getExtension(fileName: string): string {
  return (fileName.split('.').pop() ?? '').toLowerCase();
}

/**
 * Validate a policy / version document file.
 *
 * @param file The File to validate
 */
export function validatePolicyFile(file: File): FileValidationResult {
  const ext = getExtension(file.name);

  // --- Extension check ---
  if (!ALLOWED_EXTENSIONS.has(ext)) {
    console.warn(`[file-validation] Blocked upload: invalid extension ".${ext}" for file "${file.name}"`);
    return {
      valid: false,
      error: 'Please upload a PDF. Working in Word? Use File → Export → PDF, then upload here.',
    };
  }

  // --- MIME-type / extension cross-check ---
  const allowedExtsForMime = ALLOWED_MIME_TYPES[file.type];
  if (!allowedExtsForMime || !allowedExtsForMime.includes(ext)) {
    console.warn(`[file-validation] Blocked upload: MIME "${file.type}" does not match extension ".${ext}" for file "${file.name}"`);
    return {
      valid: false,
      error: `File type mismatch detected. The file "${file.name}" does not appear to be a valid ${ext.toUpperCase()} document.`,
    };
  }

  // --- Empty file check ---
  if (file.size === 0) {
    console.warn(`[file-validation] Blocked upload: empty file "${file.name}"`);
    return { valid: false, error: 'The selected file is empty. Please choose a valid document.' };
  }

  // --- Size check ---
  if (file.size > MAX_FILE_SIZE) {
    console.warn(`[file-validation] Blocked upload: file "${file.name}" exceeds 100MB limit`);
    return { 
      valid: false, 
      error: 'File exceeds 100MB limit. Please compress your PDF, optimize file size, or split into separate documents.' 
    };
  }

  return { valid: true };
}

